import { useContext, useEffect, useMemo } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Divider, List, ListItem, ListItemButton, ListItemText, useTheme } from '@mui/material';
import { useMobile } from '@/hooks/useMobile';
import { useB3Lang } from '@/lib/lang';
import { DynamicallyVariableContext } from '@/shared/dynamicallyVariable';
import { GlobalContext } from '@/shared/global';
import { type RouteItem } from '@/shared/routeList';
import { getAllowedRoutes } from '@/shared/routes';
import { store, useAppSelector } from '@/store';
import {
  setCompanyHierarchyInfoModules,
  setPagesSubsidiariesPermission,
} from '@/store/slices/company';
import { PagesSubsidiariesPermissionProps } from '@/types';
import { validatePermissionWithComparisonType } from '@/utils/b3CheckPermissions/check';
import { B3SStorage } from '@/utils/b3Storage';

import { b3HexToRgb } from '../outSideComponents/utils/b3CustomStyles';

interface B3NavProps {
  closeSidebar?: (x: boolean) => void;
}

const getSubsidiariesPermission = (routes: RouteItem[]) => {
  const subsidiariesPermission = routes.reduce((all, cur) => {
    if (cur?.subsidiariesCompanyKey) {
      const code = cur.permissionCodes?.includes(',')
        ? cur.permissionCodes.split(',')[0].trim()
        : cur.permissionCodes;

      all[cur.subsidiariesCompanyKey] = validatePermissionWithComparisonType({
        level: 3,
        code,
      });
    }

    return all;
  }, {} as PagesSubsidiariesPermissionProps);

  return subsidiariesPermission;
};

export default function B3Nav({ closeSidebar }: B3NavProps) {
  const [isMobile] = useMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const b3Lang = useB3Lang();
  const categoryPath = document.querySelector('[data-custom-category-path]')?.getAttribute('data-custom-category-path');

  const { dispatch } = useContext(DynamicallyVariableContext);
  const role = useAppSelector(({ company }) => company.customer.role);

  const { selectCompanyHierarchyId, isEnabledCompanyHierarchy } = useAppSelector(
    ({ company }) => company.companyHierarchyInfo,
  );

  const { permissions } = useAppSelector(({ company }) => company);

  const { state: globalState } = useContext(GlobalContext);
  const { quoteDetailHasNewMessages, registerEnabled } = globalState;

  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const jumpRegister = () => {
    navigate('/register');
    dispatch({
      type: 'common',
      payload: {
        globalMessageDialog: {
          open: false,
          title: '',
          message: '',
          cancelText: 'Cancel',
        },
      },
    });
  };

  const handleClick = (item: { configKey?: string; path: string }) => {
    if (role === 100) {
      dispatch({
        type: 'common',
        payload: {
          globalMessageDialog: {
            open: true,
            title: 'Registration',
            message:
              item.configKey === 'shoppingLists'
                ? 'Please create an account, or login to create a shopping list.'
                : 'To receive full access to buyer portal, please register. It will take 2 minutes.',
            cancelText: 'Cancel',
            saveText: registerEnabled ? 'Register' : '',
            saveFn: jumpRegister,
          },
        },
      });

      return;
    }

    navigate(item.path);
    if (isMobile && closeSidebar) {
      closeSidebar(false);
    }
  };

  useEffect(() => {
    let isHasSubsidiariesCompanyPermission = false;
    const { hash } = window.location;
    const url = hash.split('#')[1] || '';
    const routes = getAllowedRoutes(globalState).filter((route) => route.isMenuItem);

    if (url) {
      const routeItem = getAllowedRoutes(globalState).find((item) => {
        return matchPath(item.path, url);
      });

      if (routeItem && routeItem?.subsidiariesCompanyKey) {
        const { permissionCodes } = routeItem;

        const code = permissionCodes?.includes(',')
          ? permissionCodes.split(',')[0].trim()
          : permissionCodes;

        isHasSubsidiariesCompanyPermission = validatePermissionWithComparisonType({
          code,
          level: 3,
        });
      }
    }

    const subsidiariesPermission = getSubsidiariesPermission(routes);

    store.dispatch(setPagesSubsidiariesPermission(subsidiariesPermission));

    store.dispatch(
      setCompanyHierarchyInfoModules({
        isHasCurrentPagePermission: isHasSubsidiariesCompanyPermission,
      }),
    );
  }, [selectCompanyHierarchyId, globalState, navigate]);

  const newRoutes = useMemo(() => {
    let routes = getAllowedRoutes(globalState).filter((route) => route.isMenuItem);

    const subsidiariesPermission = getSubsidiariesPermission(routes);

    if (selectCompanyHierarchyId) {
      routes = routes.filter((route) =>
        route?.subsidiariesCompanyKey
          ? subsidiariesPermission[route.subsidiariesCompanyKey]
          : false,
      );
    } else {
      routes = routes.filter((route) => {
        if (route?.subsidiariesCompanyKey === 'companyHierarchy') {
          return isEnabledCompanyHierarchy && subsidiariesPermission[route.subsidiariesCompanyKey];
        }
        return true;
      });
    }

    return routes;

    // ignore permissions because verifyCompanyLevelPermissionByCode method with permissions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCompanyHierarchyId, permissions, globalState, isEnabledCompanyHierarchy]);

  const activePath = (path: string) => {
    if (location.pathname === path) {
      B3SStorage.set('prevPath', path);
      return true;
    }

    if (location.pathname.includes('orderDetail')) {
      const gotoOrderPath =
        B3SStorage.get('prevPath') === '/company-orders' ? '/company-orders' : '/orders';
      if (path === gotoOrderPath) return true;
    }

    if (location.pathname.includes('shoppingList') && path === '/shoppingLists') {
      return true;
    }

    if (location.pathname.includes('/quoteDetail') || location.pathname.includes('/quoteDraft')) {
      if (path === '/quotes') return true;
    }

    return false;
  };

  return (
    <List
      sx={{
        width: '100%',
        maxWidth: 360,
        bgcolor: isMobile ? 'background.paper' : 'background.default',
        color: 'black',
        '& .MuiListItem-root': {
          '& .MuiButtonBase-root.Mui-selected': {
            color: '#fff',
            bgcolor: 'primary.main',
            borderRadius: '4px',
          },
          '& .MuiButtonBase-root:hover:not(.Mui-selected)': {
            bgcolor: b3HexToRgb(primaryColor, 0.12),
            borderRadius: '4px',
          },
        },
      }}
      component="nav"
      aria-labelledby="nested-list-subheader"
    >
      {newRoutes.map((item) => {
        if (item.name === 'Quotes') {
          const { pathname } = location;
          return (
            <ListItem key={item.path} disablePadding>
              <Badge
                badgeContent={
                  quoteDetailHasNewMessages && pathname.includes('quoteDetail') ? '' : 0
                }
                variant="dot"
                sx={{
                  width: '100%',
                  '& .MuiBadge-badge.MuiBadge-dot': {
                    width: 8,
                    height: 8,
                    bgcolor: '#FFFFFF',
                    right: 14,
                    top: 22,
                  },
                }}
              >
                <ListItemButton onClick={() => handleClick(item)} selected={activePath(item.path)}>
                  <ListItemText primary={b3Lang(item.idLang)} />
                </ListItemButton>
              </Badge>
            </ListItem>
          );
        }
        if (item.path === '/user-management') {
          return null;
        }
        return (
          <ListItem key={item.path} disablePadding>
            <ListItemButton onClick={() => handleClick(item)} selected={activePath(item.path)}>
              <ListItemText primary={b3Lang(item.idLang)} />
            </ListItemButton>
          </ListItem>
        );
      })}
      {categoryPath && (
        <>
          <Divider sx={{ my: 1 }} />
          <ListItem disablePadding>
            <ListItemButton component="a" href={categoryPath} target="_top">
              <ListItemText primary={b3Lang('global.navMenu.customProducts')} />
            </ListItemButton>
          </ListItem>
        </>
      )}
    </List>
  );
}
